import React from "react";
import { Content } from "../components";
import AddEditModal, {
  AddEditModalState,
  IaddEditModalStates,
} from "../pageComponents/RTM/AddResourceModal";
import ResourceFileItem from "../pageComponents/RTM/ResourceFile";
import ResourceFileStyle from "../styles/ResourceFileItem.module.css";
import { IAddResourceNewReq } from "../hooks/rtm_resource/index.type";
import toast from "react-hot-toast";
import ResourceFileItemSkeleton from "../Skeletons/ResourceFileSkeleton";
import Pagination from "../components/Pagination";
import {
  useAddResource,
  useAllResource,
  useDeleteResource,
} from "../hooks/rtm_resource";

const ResourcesNewScreen = () => {
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [searchKey, setSearchKey] = React.useState<string>("");
  const [deleteResourceId, setDeleteResourceId] = React.useState<string>("");
  const perpageData = 18;

  const { addResourceLoading, addResource } = useAddResource();
  const { resourceData, getResourceData, resourceDataLoading } = useAllResource(
    { pageNumber, perpageData, searchKey }
  );
  const { deleteResourceLoading, deleteResource } = useDeleteResource({
    resourceId: deleteResourceId,
  });
  const [addEditModal, setAddEditModal] = React.useState<AddEditModalState>({
    status: false,
  });
  const startaddEditModalStatesValue: IaddEditModalStates = {
    resourceName: { value: "", error: "" },
    singleFile: { value: null, error: "" },
  };
  const [addEditModalStates, setAddEditModalStates] =
    React.useState<IaddEditModalStates>(startaddEditModalStatesValue);

  React.useEffect(() => {
    getResourceData();
  }, [pageNumber, searchKey]);

  const handleCloseModal = () => {
    setAddEditModal({ status: false });
    setAddEditModalStates(startaddEditModalStatesValue);
  };

  // ====================================== add and edit function start ======================================
  const handleAddResourceAPI = async (formData: IAddResourceNewReq) => {
    const data = await addResource(formData);
    if (data?.status === "success") {
      setAddEditModalStates(startaddEditModalStatesValue);
      handleCloseModal();
      getResourceData();
    }
  };

  const handleAddResource = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (
      !addEditModalStates.resourceName.value.trim() ||
      !addEditModalStates.singleFile.value
    ) {
      setAddEditModalStates((prev) => ({
        ...prev,
        resourceName: {
          ...prev.resourceName,
          error: !prev.resourceName.value.trim()
            ? "Please provide a valid name. This field cannot be empty."
            : "",
        },
        singleFile: {
          ...prev.singleFile,
          error: !prev.singleFile.value ? "A file must be selected before proceeding. Please upload a valid file." : "",
        },
      }));
      return;
    }

    const formData: IAddResourceNewReq = {
      resourceName: addEditModalStates.resourceName.value,
      singleFile: addEditModalStates.singleFile.value,
    };

    handleAddResourceAPI(formData);
  };

  // ====================================== add and edit function end ======================================

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = ["image/png", "image/jpeg", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Only PNG and JPEG files are allowed.");
        return;
      }
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error("File size should be less than 5MB.");
        return;
      }
      setAddEditModalStates((prev) => ({
        ...prev,
        singleFile: { value: file, error: "" },
      }));
    }
  };

  const onPageChange = (currentPage: { selected: number }) => {
    setPageNumber(currentPage.selected + 1);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKey(event.target.value);
  };
  // ======================================  copy file  url function end ======================================
  const handleCopyUrl = (fileUrl: string) => {
    navigator.clipboard
      .writeText(fileUrl)
      .then(() => toast.success("File URL copied to clipboard!"))
      .catch(() => toast.error("Failed to copy URL."));
  };
  // ======================================  copy file url function end ======================================

  // ======================================  Download file function start ======================================
  const handleDownloadFile = (fileUrl: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ======================================  Download file function end ======================================

  // ======================================  delete file function start ======================================

  const handleDeleteFile = async () => {
    try {
      const result = await deleteResource();
      if (result.status) {
        getResourceData();
      }
    } catch (error) {
      setDeleteResourceId("");
    }
  };
  // ======================================  delete file function end ======================================

  React.useEffect(() => {
    if (deleteResourceId) {
      handleDeleteFile();
    }
  }, [deleteResourceId]);

  return (
    <Content
      currentMenu="rtm-resource"
      headerTitle="RTM Resource"
      addBtn={true}
      addBtnClick={() => setAddEditModal({ status: true })}
      addBtnTitle="Add Resource"
      searchBox={true}
      searchPlaceholder="Search Resource"
      handleSearchChange={(e) => handleSearch(e)}
      totalReult={
        resourceData && resourceData.totalDocs > 0
          ? resourceData.totalDocs + " resources"
          : ""
      }
    >
      {addEditModal.status && (
        <AddEditModal
          loading={addResourceLoading}
          addEditModal={addEditModal}
          onCloseModal={handleCloseModal}
          addEditModalStates={addEditModalStates}
          setAddEditModalStates={setAddEditModalStates}
          handleAddEdit={handleAddResource}
          handleFileChange={handleFileChange}
        />
      )}

      <div className={ResourceFileStyle.resourceContainer}>
        {resourceDataLoading ? (
          <ResourceFileItemSkeleton count={18} />
        ) : resourceData?.files.length === 0 ? (
          <p className={ResourceFileStyle.not_found}>Resource data not found</p>
        ) : (
          resourceData?.files.map((resource) => (
            <ResourceFileItem
              id={resource._id}
              key={resource._id}
              status={resource.status}
              fileType={resource.mimeType}
              resourceName={resource.resourceName}
              fileUrl={resource?.downloadUrl}
              fileName={resource.fileName}
              onCopy={handleCopyUrl}
              onDownload={handleDownloadFile}
              onDelete={() => resource._id && setDeleteResourceId(resource._id)}
            />
          ))
        )}
      </div>
      <Pagination
        itemsPerPage={perpageData}
        totalItems={resourceData ? resourceData.totalDocs : 0}
        onPageChange={onPageChange}
      />
    </Content>
  );
};

export default ResourcesNewScreen;
